import { useQueryClient } from '@tanstack/react-query'
import React from 'react'

import { useUpdateOtherServiceMutation } from '@/components/flows/CompleteRegistrationFlow/steps/1_CompanyServices/hooks/useUpdateOtherServiceMutation'
import ActionButton from '@/components/misc/ActionButton'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { QUERY_KEYS } from '@/lib/queryKeys'

interface Props {
  /**
   * The user's text, which should represent ONE suggested other service
   */
  inputText: string
  /**
   * The text currently saved to the company record
   */
  currentOtherServicesString: string
  onSave: () => void
}

const SuggestNewServiceArea: React.FC<Props> = ({
  inputText,
  currentOtherServicesString,
  onSave,
}) => {
  const currentCompany = useCurrentCompanyOrFail()
  const queryClient = useQueryClient()
  const trimmedInputText = inputText.trim()
  // Don't allow commas, because this is going into a comma-separated list
  const cleanedInputText = trimmedInputText.replaceAll(',', '')

  const updateOtherServiceMutation = useUpdateOtherServiceMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.company(currentCompany.id),
      })

      onSave()
    },
  })

  const alreadyExists = currentOtherServicesString
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .includes(trimmedInputText.toLowerCase())

  if (alreadyExists) {
    return null
  }

  return (
    <div>
      <ActionButton
        isProcessing={updateOtherServiceMutation.isPending}
        variant="primary"
        onClick={() => {
          const newOtherServicesText = currentOtherServicesString
            ? `${currentOtherServicesString},${cleanedInputText}`
            : cleanedInputText

          updateOtherServiceMutation.mutate(newOtherServicesText)
        }}
      >
        Suggest adding "{cleanedInputText}"
      </ActionButton>

      <p className="mt-2 mb-4 text-xs">
        Click the button above to notify RQ that you'd like to add this service.
      </p>
    </div>
  )
}

export default SuggestNewServiceArea
